// Parse a pasted drawing sheet list into DrawingRef rows. Smart about mixed
// input: it detects the actual sheet-list lines inside a larger paste (title
// blocks, project info, notes, page numbers, headers) and ignores the rest.
// Handles column-delimited (tab / 2+ spaces / CSV), free-form
// ("A-101 Ground Floor Plan Rev 2"), and bare sheet numbers, with a leading
// index/bullet stripped. Discipline is inferred from the sheet code.

import type { DrawingRef } from "./types";

const DISCIPLINE: { re: RegExp; name: string }[] = [
  { re: /^(ES|ST|S)$/i, name: "Structural" },
  { re: /^(EL|E)$/i, name: "Electrical" },
  { re: /^(SA|PL|P)$/i, name: "Plumbing" },
  { re: /^(ME|MEP|M|HVAC|AC)$/i, name: "Mechanical" },
  { re: /^(CE|CI|C)$/i, name: "Civil" },
  { re: /^(AR|A)$/i, name: "Architectural" },
  { re: /^(LA|L)$/i, name: "Landscape" },
  { re: /^(FP|FA)$/i, name: "Fire" },
  { re: /^(GEN|G)$/i, name: "General" },
];

/** Infer a discipline from a sheet code by scanning its alpha segments. */
export function inferDiscipline(sheetNo: string): string {
  const segs = sheetNo.split(/[^A-Za-z]+/).filter(Boolean);
  for (const seg of segs) {
    for (const d of DISCIPLINE) if (d.re.test(seg)) return d.name;
  }
  return "";
}

// A plausible drawing sheet code, anchored to the start of the (index-stripped)
// text and only accepted when followed by end-of-string or a separator so it
// doesn't slice a longer token. Two shapes: discipline+number ("A-101", "EL301",
// "M-4.1", "A-101A") and project-prefixed ("13-GUR-G9", "52-GUR-ES-14").
const CODE_RE = /^([A-Za-z]{1,5}[-–. ]?\d{1,4}(?:[.\-]\d{1,3})?[A-Za-z]?|\d{1,3}-[A-Za-z]{1,6}(?:-[A-Za-z0-9]{1,6})+)(?=$|[\s:.\-–)])/;
// Leading pipe (table cell), index "12." or bullet, stripped before code match.
const LEAD = /^\s*\|?\s*(?:\d{1,3}[.)]\s+|[-•*·]\s+)?/;
const NOISE = /^(total|subtotal|page\b|sheet\s+\d+\s+of\b|notes?:|general notes|drawing (index|register|list|schedule)|project\b|client\b|date\b|scale\b|revision(s)? history)/i;

function extractRevision(text: string): { title: string; revision?: string } {
  const m = text.match(/[\s\-–(]*\brev\.?\s*([A-Za-z0-9]+)\)?\s*$/i);
  if (m) return { title: text.slice(0, m.index).replace(/[\s\-–,]+$/, "").trim(), revision: m[1] };
  return { title: text.trim() };
}

/** If the (index-stripped) text starts with a sheet code, return code + rest. */
function matchSheet(text: string): { number: string; title: string } | null {
  const rest = text.replace(LEAD, "");
  const m = rest.match(CODE_RE);
  if (!m) return null;
  const number = m[1].trim();
  const title = rest.slice(m[0].length).replace(/^[\s:.\-–)]+/, "").trim();
  return { number, title };
}

function isHeaderLine(line: string): boolean {
  const l = line.toLowerCase();
  return (l.includes("title") && (l.includes("rev") || l.includes("sheet") || l.includes("no"))) || /^\s*(drawing\s*(no|register|index|list|schedule)|sheet\s*(no|list))\b/.test(l);
}

export function parseDrawingList(text: string): DrawingRef[] {
  const out: DrawingRef[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || isHeaderLine(line) || NOISE.test(line.replace(LEAD, ""))) continue;

    // Column-delimited? Only accept when the first column is a real sheet code.
    let cols: string[] | null = null;
    if (line.includes("|")) cols = line.split("|").map((s) => s.trim()).filter(Boolean); // pipe/markdown table
    else if (line.includes("\t")) cols = line.split("\t").map((s) => s.trim());
    else if (/\s{2,}/.test(line)) cols = line.split(/\s{2,}/).map((s) => s.trim());
    else if (line.includes(",") && line.split(",").length >= 3) cols = line.split(",").map((s) => s.trim());

    if (cols && cols.length >= 1) {
      const col0 = cols[0].replace(LEAD, "").trim();
      if (CODE_RE.test(col0)) {
        const [, title = "", revision = "", discipline = "", date = ""] = cols.map((c) => c.trim());
        const rev = revision || extractRevision(title).revision || "";
        const cleanTitle = revision ? title : extractRevision(title).title;
        out.push({ number: col0, title: cleanTitle, revision: rev, discipline: discipline || inferDiscipline(col0), date });
        continue;
      }
      // Not a sheet row — fall through (may still match free-form), else dropped.
    }

    // Free-form: the line must START with a sheet code, else it's noise and skipped.
    const sheet = matchSheet(line);
    if (sheet) {
      const { title, revision } = extractRevision(sheet.title);
      out.push({ number: sheet.number, title, revision: revision || "", discipline: inferDiscipline(sheet.number), date: "" });
    }
  }
  return out;
}
